// ── Save/Load System for DIG DEEP ──
// LocalStorage with 3 slots, auto-save, and export/import.

import { SaveData } from '../types';

export class SaveManager {
  private readonly SAVE_PREFIX = 'digdeep_save_';
  private readonly AUTO_SAVE_KEY = 'digdeep_autosave';
  private readonly MAX_SLOTS = 3;
  private readonly CURRENT_VERSION = 1;

  /** Save game data to a numbered slot (1-3). Returns true on success. */
  saveToSlot(slot: number, data: SaveData): boolean {
    if (slot < 1 || slot > this.MAX_SLOTS) return false;

    try {
      data.timestamp = Date.now();
      const json = JSON.stringify(data);
      localStorage.setItem(this.SAVE_PREFIX + slot, json);
      return true;
    } catch (e) {
      console.error(`Failed to save to slot ${slot}:`, e);
      return false;
    }
  }

  /** Load game data from a numbered slot. Returns null if not found or invalid. */
  loadFromSlot(slot: number): SaveData | null {
    if (slot < 1 || slot > this.MAX_SLOTS) return null;

    try {
      const raw = localStorage.getItem(this.SAVE_PREFIX + slot);
      if (!raw) return null;

      const data = JSON.parse(raw);
      if (!this.validate(data)) return null;

      return data as SaveData;
    } catch (e) {
      console.error(`Failed to load slot ${slot}:`, e);
      return null;
    }
  }

  /** Delete a save slot. */
  deleteSlot(slot: number): void {
    if (slot < 1 || slot > this.MAX_SLOTS) return;

    try {
      localStorage.removeItem(this.SAVE_PREFIX + slot);
    } catch (e) {
      console.error(`Failed to delete slot ${slot}:`, e);
    }
  }

  /** Auto-save current game state. Returns true on success. */
  autoSave(data: SaveData): boolean {
    try {
      data.timestamp = Date.now();
      const json = JSON.stringify(data);
      localStorage.setItem(this.AUTO_SAVE_KEY, json);
      return true;
    } catch (e) {
      console.error('Auto-save failed:', e);
      return false;
    }
  }

  /** Load the auto-save. Returns null if not found or invalid. */
  loadAutoSave(): SaveData | null {
    try {
      const raw = localStorage.getItem(this.AUTO_SAVE_KEY);
      if (!raw) return null;

      const data = JSON.parse(raw);
      if (!this.validate(data)) return null;

      return data as SaveData;
    } catch (e) {
      console.error('Failed to load auto-save:', e);
      return null;
    }
  }

  /** Get summary info for all save slots (for the load screen UI). */
  getSlotInfo(): {
    slot: number;
    exists: boolean;
    playerName: string;
    reputation: number;
    money: number;
    timestamp: number;
  }[] {
    const slots = [];

    for (let i = 1; i <= this.MAX_SLOTS; i++) {
      try {
        const raw = localStorage.getItem(this.SAVE_PREFIX + i);
        if (raw) {
          const data = JSON.parse(raw);
          slots.push({
            slot: i,
            exists: true,
            playerName: data.playerName || 'Unknown',
            reputation: data.reputation ?? 0,
            money: data.money ?? 0,
            timestamp: data.timestamp ?? 0,
          });
        } else {
          slots.push({
            slot: i,
            exists: false,
            playerName: '',
            reputation: 0,
            money: 0,
            timestamp: 0,
          });
        }
      } catch {
        slots.push({
          slot: i,
          exists: false,
          playerName: '',
          reputation: 0,
          money: 0,
          timestamp: 0,
        });
      }
    }

    return slots;
  }

  /** Export save data as a base64-encoded JSON string (for clipboard sharing). */
  exportSave(data: SaveData): string {
    const json = JSON.stringify(data);
    return btoa(unescape(encodeURIComponent(json)));
  }

  /** Import save data from a base64-encoded JSON string. Returns null if invalid. */
  importSave(json: string): SaveData | null {
    try {
      const decoded = decodeURIComponent(escape(atob(json.trim())));
      const data = JSON.parse(decoded);

      if (!this.validate(data)) {
        console.error('Imported save data failed validation.');
        return null;
      }

      return data as SaveData;
    } catch (e) {
      console.error('Failed to import save:', e);
      return null;
    }
  }

  /** Download save data as a .json file via temporary anchor element. */
  downloadSave(data: SaveData, filename?: string): void {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const name = filename || `digdeep_${data.playerName || 'save'}_${Date.now()}.json`;

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();

    // Clean up
    setTimeout(() => {
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    }, 100);
  }

  /** Check whether any save data exists (slots or auto-save). */
  hasSaves(): boolean {
    try {
      if (localStorage.getItem(this.AUTO_SAVE_KEY)) return true;

      for (let i = 1; i <= this.MAX_SLOTS; i++) {
        if (localStorage.getItem(this.SAVE_PREFIX + i)) return true;
      }
    } catch {
      // localStorage unavailable
    }

    return false;
  }

  /** Validate that an object has the required SaveData shape. */
  private validate(data: any): data is SaveData {
    if (data == null || typeof data !== 'object') return false;

    // Required scalar fields
    if (typeof data.version !== 'number') return false;
    if (typeof data.playerName !== 'string') return false;
    if (typeof data.money !== 'number') return false;
    if (typeof data.reputation !== 'number') return false;

    // Required array field
    if (!Array.isArray(data.artifacts)) return false;

    // Version compatibility check
    if (data.version > this.CURRENT_VERSION) {
      console.warn(
        `Save version ${data.version} is newer than supported version ${this.CURRENT_VERSION}.`
      );
    }

    return true;
  }
}

/** Singleton save manager instance. */
export const saveManager = new SaveManager();
